import React from 'react';
import { getStrategyMeta } from './shared/strategyMeta';
import './StrategyFilterModal.css';

/* Catalog of selectable strategy types. Order mirrors the creation wizard /
   trade-group list. Label, icon, and card-accent class come from STRATEGY_META
   (the single source of truth); only the modal-specific blurb lives here. Adding
   a strategy = one STRATEGY_META entry + one description line. */
const STRATEGY_DESCRIPTIONS: Array<{ type: string; description: string }> = [
  { type: 'REGULAR_LADDER', description: 'Traditional DCA strategy for market dips' },
  { type: 'FUTURE_LADDER', description: 'Conditional trades above current price' },
  { type: 'MOMENTUM_SWING', description: 'Trend-following with swing optimization' },
  { type: 'VOLATILITY_LADDER', description: 'Adaptive position sizing based on volatility' },
  { type: 'BOLLINGER_REVERSION', description: 'Mean reversion on lower band touches in ranging markets' },
  { type: 'RSI_EXTREMES', description: 'Mean reversion on RSI oversold in low volatility markets' },
  { type: 'MACD_REVERSION', description: 'Mean reversion on MACD histogram recovery' },
  { type: 'MACD_MOMENTUM', description: 'Trend continuation on a bullish MACD crossover with an expanding histogram' },
  { type: 'VWAP_REVERSION', description: 'Volume-weighted mean reversion; entry on σ-band lower touch' },
  { type: 'ICHIMOKU_CLOUD', description: 'Trend-following long entries on 5-component confirmation above the cloud' },
  { type: 'PIVOT_POINT_TRADING', description: 'Long entries on bullish bounces at pivot support; three-tier exits at PP/R1/R2' },
  { type: 'MA_CROSSOVER', description: 'Long-only trend following on a confirmed bullish fast/slow MA crossover' },
  { type: 'SUPPORT_RETEST_DIP_LADDER', description: 'Long entries on a confirmed support-level retest after a breakout' },
  { type: 'ORCHESTRATED_LADDER', description: 'Regime-gated buy-ladder deployer; manages the full deploy→manage→teardown lifecycle' },
];

interface StrategyFilterModalProps {
  /** Currently selected strategy types (multi-select). */
  selected: string[];
  /** Called with the new selection each time a card is toggled (applies live). */
  onChange: (types: string[]) => void;
  /** Close the modal — the page is already filtered by the live selection. */
  onClose: () => void;
}

/**
 * Multi-select strategy-type filter presented as a modal of described cards.
 * Toggling a card applies immediately to the trade-groups table (the selection
 * is the live filter); closing the modal just dismisses it.
 */
const StrategyFilterModal: React.FC<StrategyFilterModalProps> = ({ selected, onChange, onClose }) => {
  const toggle = (type: string) => {
    onChange(selected.includes(type) ? selected.filter(t => t !== type) : [...selected, type]);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content strategy-filter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Filter by Strategy Type</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          {/*<p className="modal-description">*/}
          {/*  Select one or more strategies to filter the trade groups below. Click a selected card*/}
          {/*  again to remove it.*/}
          {/*</p>*/}

          <div className="strategy-filter-grid">
            {STRATEGY_DESCRIPTIONS.map(({ type, description }) => {
              const { label, Icon, cardClass } = getStrategyMeta(type);
              const isSelected = selected.includes(type);
              return (
                <button
                  key={type}
                  type="button"
                  className={`strategy-filter-card ${cardClass} ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggle(type)}
                  aria-pressed={isSelected}
                >
                  {isSelected && <span className="strategy-filter-check" aria-hidden="true">✓</span>}
                  <div className="strategy-filter-icon"><Icon size={28} /></div>
                  <h3>{label}</h3>
                  <p>{description}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="modal-pill-button modal-pill-cancel"
            onClick={() => onChange([])}
            disabled={selected.length === 0}
            type="button"
          >
            Clear all
          </button>
          <button className="modal-pill-button modal-pill-primary" onClick={onClose} type="button">
            Done{selected.length > 0 ? ` (${selected.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StrategyFilterModal;
